import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { UnitDelete } from "@shared/models/analytics/unit"
import type { Course } from "@shared/models/course"
import type { Planbook } from "@shared/models/planbook"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import type { PatchModel } from "@shared/models/patch"
import Patch from "@shared/models/patch-updater"
import { consolidateCourseDatesOff, constructUnitsAndDates } from "cc-frontend/lib/unit-utils"
import type { PreparedAction } from "@shared/actions/action-doc-builder"
import { cloneDeep, includes, map, partial, pull } from "lodash-es"
import type { Narrative } from "@shared/actions/create-narrative"

interface Payload {
  unitId: string
  course: Course
  planbook: Planbook
  rotationCalendar: RotationCalendar
  shouldDeleteLessons: boolean
}

export function prepareDeleteUnit(payload: Payload): PreparedAction {
  return {
    name: DELETE_UNIT.name,
    payload: payload,
    patches: DELETE_UNIT.patches(payload),
    undoPatches: DELETE_UNIT.undoPatches(payload),
    narrative: partial(DELETE_UNIT.narrative, payload),
  }
}

export const DELETE_UNIT = {
  name: "DELETE_UNIT",

  patches(payload: Payload): Array<PatchModel> {
    let newCourseDates = map(payload.course.attributes.calendar.dates, (courseDate) => {
      let cd = courseDate
      if (cd.type === "course-date-custom") {
        if (includes(cd.attributes.unitStart, payload.unitId)) {
          cd = cloneDeep(cd)
          pull(cd.attributes.unitStart, payload.unitId)
        }
        if (includes(cd.attributes.unitEnd, payload.unitId)) {
          cd = cloneDeep(cd)
          pull(cd.attributes.unitEnd, payload.unitId)
        }
        if (payload.shouldDeleteLessons && includes(cd.attributes.unitIds, payload.unitId)) {
          cd = cloneDeep(cd)
          cd.attributes.cardStackId = null
        }
        if (includes(cd.attributes.unitIds, payload.unitId)) {
          cd = cloneDeep(cd)
          pull(cd.attributes.unitIds, payload.unitId)
        }
      }
      return cd
    })

    let consolidatedCourseDatesOff = consolidateCourseDatesOff(
      payload.course,
      payload.planbook,
      payload.rotationCalendar
    )

    let { courseDates, units } = constructUnitsAndDates(
      newCourseDates,
      consolidatedCourseDatesOff,
      payload.rotationCalendar.attributes.schoolDays
    )

    return Patch.create("course", payload.course.id)
      .inc(`attributes.calendar.version`, 1)
      .set(`attributes.calendar.dates`, courseDates)
      .set(`attributes.calendar.units`, units).wrapInArray
  },

  undoPatches(payload: Payload): Array<PatchModel> {
    return Patch.create("course", payload.course.id)
      .inc(`attributes.calendar.version`, 1)
      .set(
        "attributes.calendar.dates",
        JSON.parse(JSON.stringify(payload.course.attributes.calendar.dates))
      )
      .set(
        "attributes.calendar.units",
        JSON.parse(JSON.stringify(payload.course.attributes.calendar.units))
      ).wrapInArray
  },

  narrative(payload: Payload, findFn: FindFn, userId: string): Narrative | null {
    let models = {
      course: payload.course,
      planbook: payload.planbook,
      unit: findFn("card-stack", payload.unitId) || findFn("card-stack-summary", payload.unitId),
    }
    let newEvent: UnitDelete = {
      ...createBlankEvent(),
      root_id: payload.planbook.id,
      root_type: "planbook",
      date_plan_scheduled_for: null,
      primary_verb: "delete",
      primary_possessive: getPossessiveForPlanbook(findFn, userId, payload.planbook.id),

      primary_object_type: "unit",
      primary_object_id: payload.unitId,
      primary_object_specifier: null,
      primary_object_id_and_ancestor_ids: [payload.planbook.id, payload.course.id],

      adverbial_verb: null,

      adverbial_object_type: null,
      adverbial_object_id: null,
      adverbial_object_specifier: null,
      adverbial_object_id_and_ancestor_ids: [payload.planbook.id, payload.course.id],
    }

    return {
      title: "Deleted Unit",
      titleForAnalytics: "Unit - Remove",
      description: `Deleted "${models.unit.attributes.title}"`,
      context: {
        unitTitle: models.unit.attributes.title,
        courseTitle: models.course.attributes.title,
        planbookTitle: models.planbook.attributes.title,
        unitId: models.unit.id,
        courseId: models.course.id,
        planbookId: models.planbook.id,
      },
      activity: {
        actor: `user-${userId}`,
        verb: "edited",
        object: `course-${models.course.id}`,
        to: [
          `course-edits:course-${models.course.id}`,
          `planbook-edits:planbook-${models.planbook.id}`,
        ],
      },
      newAnalyticsEvents: [newEvent],
    }
  },
}

export default DELETE_UNIT
